/**
 * MyExchanges
 * User's connected exchange accounts with sync management.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLingui } from '@lingui/react';
import { useApiClient } from '../api/client';
import type { UserExchange, SyncJobSSEEvent } from '../api/client';
import { parseDbDate } from '../lib/date';

const SYNC_BADGE: Record<string, string> = {
  never: 'badge-neutral',
  syncing: 'badge-warning',
  ok: 'badge-success',
  error: 'badge-error',
};

export default function MyExchanges() {
  const { _ } = useLingui();

  const SYNC_LABEL: Record<string, string> = {
    never: _({ id: 'exchanges.myList.syncStatus.never' }),
    syncing: _({ id: 'exchanges.myList.syncStatus.syncing' }),
    ok: _({ id: 'exchanges.myList.syncStatus.ok' }),
    error: _({ id: 'exchanges.myList.syncStatus.error' }),
  };

  const api = useApiClient();
  const [exchanges, setExchanges] = useState<UserExchange[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function reload() {
    setLoading(true);
    try {
      setExchanges(await api.listUserExchanges());
    } catch {
      setError(_({ id: 'exchanges.myList.error.listFetch' }));
    } finally {
      setLoading(false);
    }
  }

  async function handleSync(id: string) {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setSyncingId(id);
    setError(null);
    setExchanges((prev) => prev.map((e) => (e.id === id ? { ...e, syncStatus: 'syncing' } : e)));

    try {
      const { syncJobId } = await api.syncExchange(id);

      await api.streamSyncJob(
        id,
        syncJobId,
        (event: SyncJobSSEEvent) => {
          setExchanges((prev) =>
            prev.map((e) =>
              e.id === id
                ? {
                    ...e,
                    syncStatus: event.status as UserExchange['syncStatus'],
                    syncErrorMsg: event.errorMsg,
                    lastSyncedAt: event.completedAt ?? e.lastSyncedAt,
                  }
                : e,
            ),
          );
        },
        ctrl.signal,
      );

      // Reload to get fresh lastSyncedAt from DB
      await reload();
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      setError(_({ id: 'exchanges.myList.error.syncFailed' }));
    } finally {
      setSyncingId(null);
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteTargetId) return;
    setDeleting(true);
    try {
      await api.deleteUserExchange(deleteTargetId);
      setDeleteTargetId(null);
      await reload();
    } catch {
      setError(_({ id: 'exchanges.myList.error.deleteFailed' }));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between min-h-7">
        <h1 className="text-xl font-bold hidden lg:block">{_({ id: 'exchanges.myList.title' })}</h1>
        <Link to="/exchanges/new" className="btn btn-primary btn-sm ml-auto lg:ml-0">
          {_({ id: 'exchanges.myList.addButton' })}
        </Link>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button className="btn btn-ghost btn-xs" onClick={() => setError(null)}>
            ✕
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <span className="loading loading-spinner loading-lg" />
        </div>
      ) : exchanges.length === 0 ? (
        <div className="card bg-base-100 shadow">
          <div className="card-body items-center text-center">
            <h2 className="card-title">{_({ id: 'exchanges.myList.empty.title' })}</h2>
            <p className="text-base-content/60">
              {_({ id: 'exchanges.myList.empty.description' })}
            </p>
            <Link to="/exchanges/new" className="btn btn-primary mt-2">
              {_({ id: 'exchanges.myList.addButton' })}
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {exchanges.map((ex) => (
            <div key={ex.id} className="card bg-base-100 shadow">
              <div className="card-body">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="card-title text-base">{ex.exchangeName ?? ex.exchangeSlug}</h2>
                    {ex.label && <p className="text-sm text-base-content/60">{ex.label}</p>}
                  </div>
                  <span
                    className={`badge badge-sm ${SYNC_BADGE[ex.syncStatus] ?? 'badge-neutral'}`}
                  >
                    {SYNC_LABEL[ex.syncStatus] ?? ex.syncStatus}
                  </span>
                </div>

                {ex.syncErrorMsg && (
                  <div className="alert alert-error alert-sm py-1 text-xs">{ex.syncErrorMsg}</div>
                )}

                <div className="text-xs text-base-content/50">
                  {ex.lastSyncedAt
                    ? _({
                        id: 'exchanges.myList.card.lastSync',
                        values: { date: parseDbDate(ex.lastSyncedAt).toLocaleString('tr-TR') },
                      })
                    : _({ id: 'exchanges.myList.card.neverSynced' })}
                </div>

                <div className="card-actions justify-end mt-2 items-center">
                  <button
                    className="btn btn-ghost btn-sm text-error"
                    onClick={() => setDeleteTargetId(ex.id)}
                  >
                    {_({ id: 'exchanges.myList.card.deleteButton' })}
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => void handleSync(ex.id)}
                    disabled={syncingId === ex.id || ex.syncStatus === 'syncing'}
                  >
                    {syncingId === ex.id || ex.syncStatus === 'syncing' ? (
                      <span className="loading loading-spinner loading-xs" />
                    ) : (
                      _({ id: 'exchanges.myList.card.syncButton' })
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Silme onay modalı */}
      {deleteTargetId && (
        <div className="modal modal-open">
          <div className="modal-box max-w-sm">
            <h3 className="font-bold text-lg">{_({ id: 'exchanges.myList.deleteModal.title' })}</h3>
            <p className="py-4 text-base-content/70">
              {_({ id: 'exchanges.myList.deleteModal.body' })}
            </p>
            <div className="modal-action">
              <button
                className="btn btn-ghost"
                onClick={() => setDeleteTargetId(null)}
                disabled={deleting}
              >
                {_({ id: 'exchanges.myList.deleteModal.cancel' })}
              </button>
              <button
                className="btn btn-error"
                onClick={() => void handleDeleteConfirm()}
                disabled={deleting}
              >
                {deleting ? <span className="loading loading-spinner loading-xs" /> : null}
                {_({ id: 'exchanges.myList.deleteModal.confirm' })}
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => !deleting && setDeleteTargetId(null)} />
        </div>
      )}
    </div>
  );
}
